import { AfterViewInit, Component } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet-routing-machine';
import { AuthenticationService } from '../../authentication/authentication/authentication.service';
import { GeographicRouteService } from 'src/app/services/geographic-route.service';
import { WarehouseService } from 'src/app/services/warehouse.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-geographic-map-routes',
  templateUrl: './geographic-map-routes.component.html',
  styleUrls: ['./geographic-map-routes.component.scss']
})
export class GeographicMapRoutesComponent implements AfterViewInit{
  private map!: L.Map;
  companyId = this.authService.getCurrentCompanyId();
  selectedDate = this.getToday();
  selectedWarehouse: number | 'ALL' = 'ALL';
  warehouses: any[] = [];
  showWarehouseLabels: boolean = true;

  constructor(
    private authService: AuthenticationService,
    private geoService: GeographicRouteService,
    private warehouseService: WarehouseService,
    private toastService: ToastrService
  ) {}

  private markerIcon = L.icon({
    iconUrl: 'assets/icons/pin.png',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initMap();
      this.getWarehouses(() => this.loadRoutesFromBackend());
    }, 0);
  }

  getToday() {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private initMap(): void {
    const bounds = L.latLngBounds(L.latLng(13.5, -92.5), L.latLng(17.8, -88.0));
    this.map = L.map('map', {
      center: [14.9, -90.8],
      zoom: 8,
      maxBounds: bounds,
      maxBoundsViscosity: 1.0,
      minZoom: 7
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    // this.map.on('zoom', () => {
    //   const zoom = this.map.getZoom();
    //   const scale = Math.pow(zoom / 8, 0.9);
    //   const labels = document.querySelectorAll('.warehouse-label') as NodeListOf<HTMLDivElement>;
    //   labels.forEach(label => {
    //     label.style.transform = `scale(${scale})`;
    //   });
    // });
  }

  getWarehouses(onComplete?: () => void): void {
    this.warehouseService.getWarehouses().subscribe({
      next: (response: ResponseObject) => {
        this.warehouses = response.data || [];
        this.warehouses.unshift({ id: 'ALL', name: 'Todas las bodegas' });
        if (onComplete) onComplete();
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error('Error al cargar bodegas');
      }
    });
  }

  loadRoutesFromBackend(): void {
    const timestamp = new Date(this.selectedDate).getTime() + (6 * 60 * 60 * 1000);

    this.geoService.getRoutesByCompanyAndDateClientInvoice(Number(this.companyId), timestamp).subscribe({
      next: (response) => {
        this.clearMap();
        const routesByWarehouse = response.data;

        Object.keys(routesByWarehouse).forEach((warehouseId, index) => {
          if (this.selectedWarehouse !== 'ALL' && Number(warehouseId) !== this.selectedWarehouse) return;

          const points = routesByWarehouse[warehouseId];
          if (points && points.length > 0) {
            const waypoints = points.map(p => ({
              lat: p.geographicRoutePoint.latitude,
              lng: p.geographicRoutePoint.longitude
            }));

            const color = this.getColor(index);
            const warehouseName =
              this.warehouses.find(w => w.id === Number(warehouseId))?.name || `Bodega`;
            this.drawSilentRoute(waypoints, color, warehouseName, points);
          }
        });
      },
      error: (err) => {
        console.error('Error al cargar rutas:', err);
        this.toastService.error('No se pudieron cargar las rutas.');
      }
    });
  }


  private clearMap(): void {
    this.map.eachLayer(layer => {
      if (layer instanceof L.Marker || (layer as any).options?.styles) {
        this.map.removeLayer(layer);
      }
    });
  }

  private drawSilentRoute(
    waypoints: { lat: number; lng: number }[],
    color = '#0077ff',
    warehouseName?: string,
    points?: any[]
  ): void {
    const osrm = L.Routing.osrmv1();

    const groupedPoints = this.groupPointsByCoordinates(points || []);

    Object.keys(groupedPoints).forEach((coordKey, index) => {
      const group = groupedPoints[coordKey];
      const [lat, lng] = coordKey.split(',').map(Number);

      const marker = L.marker([lat, lng], { icon: this.markerIcon }).addTo(this.map);

      let popupHtml = `<div style="text-align:center;">`;

      if (group.length > 1) {
        popupHtml += `
          <strong>${group.length} facturas en este punto</strong><br>
          <div style="margin-top:6px;">`;

        group.forEach(g => {
          const series = g.series || '';
          const number = g.number || '';
          const label = (series && number)
            ? `${series} - ${number}`
            : `Factura ID ${g.geographicRoutePoint.id}`;

          popupHtml += `
            <button id="details-${g.geographicRoutePoint.id}" 
              style="margin:3px; padding:3px 6px; background-color:#007bff; color:white; border:none; border-radius:4px; cursor:pointer;">
              Ver ${label}
            </button><br>`;
        });

        popupHtml += `</div>`;
      } else {
        const g = group[0];
        const series = g.series || '';
        const number = g.number || '';
        const label = (series && number)
          ? `${series} - ${number}`
          : `Factura ID ${g.geographicRoutePoint.id}`;

        popupHtml += `
          <strong>Acciones</strong><br>
          <button id="details-${g.geographicRoutePoint.id}" 
            style="margin:4px; padding:4px 8px; background-color:#007bff; color:white; border:none; border-radius:4px; cursor:pointer;">
            Ver ${label}
          </button><br>`;
      }

      popupHtml += `
        <button id="maps-${index}" 
          style="margin:4px; padding:4px 8px; background-color:#28a745; color:white; border:none; border-radius:4px; cursor:pointer;">
          Abrir en Maps
        </button>
      </div>`;

      marker.bindPopup(popupHtml);

      marker.on('popupopen', () => {
        group.forEach(g => {
          const btn = document.getElementById(`details-${g.geographicRoutePoint.id}`);
          if (btn) {
            btn.addEventListener('click', () => {
              this.getDetailsByGeographicPointId(g.geographicRoutePoint.id);
            });
          }
        });

        const mapsBtn = document.getElementById(`maps-${index}`);
        if (mapsBtn) {
          mapsBtn.addEventListener('click', () => {
            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
          });
        }
      });
    });

    osrm.route(waypoints.map(p => ({ latLng: L.latLng(p.lat, p.lng) })), (err, routes) => {
      if (routes && routes[0]) {
        L.Routing.line(routes[0], { styles: [{ color, weight: 4 }] }).addTo(this.map);

        if (this.showWarehouseLabels && warehouseName) {
          const midIndex = Math.floor(waypoints.length / 2);
          const midPoint = waypoints[midIndex];

          const label = L.divIcon({
            html: `
              <div class="warehouse-label" style="
                background-color: ${color};
                color: white;
                padding: 4px 10px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 700;
                white-space: nowrap;
                text-align: center;
                display: inline-block;
                transform: rotate(0deg);
                box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                border: 1px solid rgba(255,255,255,0.3);
              ">
                ${warehouseName}
              </div>
            `,
            className: ''
          });

          L.marker([midPoint.lat, midPoint.lng], { icon: label, interactive: false }).addTo(this.map);
        }
      } else if (err) {
        console.warn('No se pudo trazar la ruta:', err);
      }
    });
  }


  private getColor(index: number): string {
    const hue = (index * 47.3) % 360;
    return `hsl(${hue}, 75%, 55%)`;
  }

  selectedInvoice: any = null;
  showInvoiceModal: boolean = false;

  getDetailsByGeographicPointId(geoPointId) {
    this.geoService.getDetailsByRouteId(geoPointId).subscribe({
      next: (response: ResponseObject) => {
        this.selectedInvoice = response.data;
        this.showInvoiceModal = true;
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error('Error al cargar los detalles de la factura');
      }
    });
  }

  private groupPointsByCoordinates(points: any[]): Record<string, any[]> {
    const groups: Record<string, any[]> = {};
    points.forEach(p => {
      const lat = p.geographicRoutePoint.latitude;
      const lng = p.geographicRoutePoint.longitude;
      const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return groups;
  }
}
